<template>
  <header class="natcon-header-wrapper">
    <!-- Executive Command Bar Container -->
    <div class="natcon-topbar shadow-lg">
      <!-- Background Decorative Layers Wrapper (Clips Glows & Grids to Header Rounded Corners) -->
      <div class="header-bg-decor" aria-hidden="true">
        <!-- Layer 1: Fine Light Digital Tech Grid -->
        <div class="header-tech-grid"></div>

        <!-- Layer 2: Atmospheric Radial Glows -->
        <div class="header-atmospheric-glows">
          <div class="atmos-glow glow-aqua-left"></div>
          <div class="atmos-glow glow-navy-depth"></div>
          <div class="atmos-glow glow-gold-right"></div>
        </div>

        <!-- Layer 3: Thin Flowing Energy Curves (SVG Lines) -->
        <svg
          class="header-light-curves-svg"
          viewBox="0 0 1200 80"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient
              id="hdrCurveGrad1"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stop-color="#FFFFFF" stop-opacity="0" />
              <stop offset="30%" stop-color="#0097D7" stop-opacity="0.35" />
              <stop offset="70%" stop-color="#56BDA3" stop-opacity="0.3" />
              <stop offset="100%" stop-color="#FFFFFF" stop-opacity="0" />
            </linearGradient>
            <linearGradient
              id="hdrCurveGrad2"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stop-color="#FFFFFF" stop-opacity="0" />
              <stop offset="40%" stop-color="#EDBE38" stop-opacity="0.25" />
              <stop offset="80%" stop-color="#0097D7" stop-opacity="0.35" />
              <stop offset="100%" stop-color="#FFFFFF" stop-opacity="0" />
            </linearGradient>
          </defs>
          <path
            class="hdr-curve curve-1"
            d="M 0 60 Q 300 10, 600 40 T 1200 20"
            stroke="url(#hdrCurveGrad1)"
          />
          <path
            class="hdr-curve curve-2"
            d="M 0 25 Q 400 70, 800 20 T 1200 65"
            stroke="url(#hdrCurveGrad2)"
          />
        </svg>

        <!-- Layer 4: Shimmer Reflection Wave Sweep -->
        <div class="header-shimmer-wave"></div>
      </div>

      <!-- Left Side: Mobile Toggle & Executive Page Identity -->
      <div
        class="d-flex align-items-center position-relative z-index-2 header-left-identity"
      >
        <!-- Sidebar Toggle Button -->
        <button
          id="sidebarToggleTop"
          class="sidebar-toggle-btn mr-3"
          @click="$emit('toggle-sidebar')"
          title="Toggle Navigation Menu"
        >
          <i class="fas fa-bars"></i>
        </button>

        <!-- NATCON 2026 Event Status Badge -->
        <div
          class="header-brand-badge mr-3 d-none d-md-flex align-items-center"
        >
          <span class="badge-dot animate-pulse-gold"></span>
          <span class="badge-text">NATCON 2026</span>
        </div>

        <!-- Page Title & Descriptor -->
        <div class="page-title-group">
          <h1 class="page-title mb-0" v-text="$route.name || 'Dashboard'"></h1>
          <span class="page-subtitle d-none d-xl-inline"
            >Event Operations & Management Portal</span
          >
        </div>
      </div>

      <!-- Right Side: Executive Control Cluster -->
      <div
        class="d-flex align-items-center ml-auto position-relative z-index-2 header-right-controls"
      >
        <!-- Secure Admin Portal System Status Chip -->
        <div
          class="system-status-chip mr-3 d-none d-lg-flex align-items-center"
        >
          <span class="status-seafoam-dot mr-2"></span>
          <i class="fas fa-shield-alt mr-2 text-seafoam"></i>
          <span>Secure Admin Portal</span>
        </div>

        <!-- Quick Notifications Control -->
        <div
          class="quick-icon-btn mr-3 d-none d-sm-flex align-items-center justify-content-center"
        >
          <i class="far fa-bell"></i>
          <span class="icon-indicator"></span>
        </div>

        <div class="header-divider d-none d-sm-block"></div>

        <!-- User Profile Dropdown -->
        <div
          class="dropdown no-arrow ml-2 position-relative"
          ref="profileDropdown"
        >
          <a
            class="user-profile-card dropdown-toggle"
            href="#"
            id="userDropdown"
            role="button"
            @click.prevent="toggleDropdown"
            :class="{ show: dropdownOpen }"
            aria-haspopup="true"
            :aria-expanded="dropdownOpen"
          >
            <div class="user-avatar-wrapper mr-2">
              <img
                class="user-avatar rounded-circle"
                src="img/undraw_profile.svg"
                alt="User Avatar"
              />
              <span class="avatar-status-online"></span>
            </div>

            <div class="user-info-text mr-1">
              <span class="user-name">{{ DisplayName || "Admin User" }}</span>
              <span class="user-role">NATCON Officer</span>
            </div>

            <i
              class="fas fa-chevron-down ml-2 dropdown-arrow text-white-50 small"
            ></i>
          </a>

          <!-- Dropdown Menu -->
          <div
            class="dropdown-menu dropdown-menu-right custom-profile-dropdown animated--slide-in shadow-lg"
            :class="{ show: dropdownOpen }"
            aria-labelledby="userDropdown"
          >
            <div class="dropdown-user-header px-4 py-3">
              <p class="font-weight-bold mb-0 text-navy">
                {{ DisplayName || "Admin User" }}
              </p>
              <span class="small text-muted">NATCON Event Operations</span>
            </div>
            <div class="dropdown-divider my-1"></div>

            <a class="dropdown-item py-2.5" href="#" @click.prevent>
              <i class="fas fa-user-circle mr-3 text-navy"></i>
              <span>Profile Settings</span>
            </a>

            <a class="dropdown-item py-2.5" href="#" @click.prevent>
              <i class="fas fa-sliders-h mr-3 text-navy"></i>
              <span>System Preferences</span>
            </a>

            <div class="dropdown-divider my-1"></div>

            <a
              class="dropdown-item logout-item py-2.5"
              href="#"
              @click.prevent="LogoutUser"
            >
              <i class="fas fa-sign-out-alt mr-3 text-danger"></i>
              <span>Sign Out</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import Common from "@/Common.vue";

export default {
  name: "TopBarMenu",
  mixins: [Common],
  data() {
    return {
      ProfileImagePath: "",
      DisplayName: "",
      dropdownOpen: false,
    };
  },
  beforeMount() {
    var user = this.$session.get("user");
    user = user == null || user == undefined ? {} : user;
    var DisplayName = user.hasOwnProperty("DisplayName")
      ? user.DisplayName
      : "";
    this.DisplayName = DisplayName;
  },
  mounted() {
    document.addEventListener("click", this.closeDropdownOutside);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.closeDropdownOutside);
  },
  methods: {
    toggleDropdown(e) {
      if (e) e.stopPropagation();
      this.dropdownOpen = !this.dropdownOpen;
    },
    closeDropdownOutside(e) {
      if (
        this.$refs.profileDropdown &&
        !this.$refs.profileDropdown.contains(e.target)
      ) {
        this.dropdownOpen = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.natcon-header-wrapper {
  width: 100%;
  position: relative;
  z-index: 900;
  background-color: transparent;
  padding: 0.75rem 2rem 0;
}

@media (max-width: 768px) {
  .natcon-header-wrapper {
    padding: 0.5rem 0.5rem 0;
  }
}

/* Executive Command Bar Base Surface */
.natcon-topbar {
  position: relative;
  background: linear-gradient(135deg, #1b49a3 0%, #3a67b1 50%, #0a0f29 100%);
  height: 4.75rem;
  width: 100%;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  padding: 0 1.5rem;
  display: flex;
  align-items: center;
  overflow: visible !important;
  box-shadow: 0 12px 35px rgba(10, 15, 41, 0.22) !important;
}

/* Background Decorative Layers Wrapper (Clips Glows & Grids to Header Rounded Corners) */
.header-bg-decor {
  position: absolute;
  inset: 0;
  overflow: hidden;
  border-radius: 20px;
  pointer-events: none;
  z-index: 1;
}

/* Layer 1: Fine Light Digital Tech Grid */
.header-tech-grid {
  position: absolute;
  inset: 0;
  background-image: linear-gradient(
      rgba(255, 255, 255, 0.035) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(255, 255, 255, 0.035) 1px, transparent 1px);
  background-size: 24px 24px;
  pointer-events: none;
  z-index: 1;
  opacity: 0.8;
  animation: gridPulse 10s infinite ease-in-out alternate;
}

@keyframes gridPulse {
  0% {
    opacity: 0.5;
  }
  100% {
    opacity: 0.95;
  }
}

/* Layer 2: Atmospheric Radial Glows */
.header-atmospheric-glows {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 1;

  .atmos-glow {
    position: absolute;
    border-radius: 50%;

    &.glow-aqua-left {
      width: 320px;
      height: 320px;
      top: -120px;
      left: 10%;
      background: radial-gradient(
        circle,
        rgba(0, 151, 215, 0.3) 0%,
        transparent 70%
      );
      animation: floatGlowLeft 12s infinite ease-in-out alternate;
    }
    &.glow-navy-depth {
      width: 400px;
      height: 400px;
      bottom: -180px;
      left: 45%;
      background: radial-gradient(
        circle,
        rgba(10, 15, 41, 0.6) 0%,
        transparent 70%
      );
    }
    &.glow-gold-right {
      width: 250px;
      height: 250px;
      top: -80px;
      right: 5%;
      background: radial-gradient(
        circle,
        rgba(237, 190, 56, 0.22) 0%,
        transparent 70%
      );
      animation: floatGlowRight 14s infinite ease-in-out alternate-reverse;
    }
  }
}

@keyframes floatGlowLeft {
  0% {
    transform: translateY(0) scale(1);
  }
  100% {
    transform: translateY(12px) scale(1.1);
  }
}

@keyframes floatGlowRight {
  0% {
    transform: translateY(0) scale(1);
  }
  100% {
    transform: translateY(-10px) scale(1.08);
  }
}

/* Layer 3: Thin Flowing Energy Curves (SVG Lines) */
.header-light-curves-svg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
  opacity: 0.65;

  .hdr-curve {
    fill: none;
    stroke-width: 1.2px;
    stroke-dasharray: 180 350;
    animation: curveFlow 16s linear infinite;

    &.curve-1 {
      animation-duration: 14s;
    }
    &.curve-2 {
      stroke-width: 1px;
      animation-duration: 18s;
      animation-delay: -5s;
    }
  }
}

@keyframes curveFlow {
  0% {
    stroke-dashoffset: 800;
    opacity: 0.3;
  }
  50% {
    opacity: 0.8;
  }
  100% {
    stroke-dashoffset: -800;
    opacity: 0.3;
  }
}

/* Layer 4: Shimmer Reflection Wave Sweep */
.header-shimmer-wave {
  position: absolute;
  top: 0;
  left: -100%;
  width: 200%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.02) 40%,
    rgba(255, 255, 255, 0.14) 50%,
    rgba(255, 255, 255, 0.02) 60%,
    transparent 100%
  );
  transform: skewX(-20deg);
  pointer-events: none;
  animation: headerShimmerSweep 10s infinite linear;
  z-index: 1;
}

@keyframes headerShimmerSweep {
  0% {
    transform: translateX(0) skewX(-20deg);
  }
  100% {
    transform: translateX(100%) skewX(-20deg);
  }
}

.z-index-2 {
  z-index: 2;
}

/* Left Page Identity Animations & Layout */
.header-left-identity {
  animation: headerLeftFade 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

@keyframes headerLeftFade {
  0% {
    opacity: 0;
    transform: translateX(-15px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
}

.sidebar-toggle-btn {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.22);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  backdrop-filter: blur(8px);
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    color: #ffffff;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
    border-color: rgba(255, 255, 255, 0.35);
  }
}

/* NATCON 2026 Premium Event Status Badge */
.header-brand-badge {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(237, 190, 56, 0.4);
  padding: 0.35rem 0.95rem;
  border-radius: 20px;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 12px rgba(10, 15, 41, 0.15);

  .badge-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #edbe38;
    display: inline-block;
    margin-right: 8px;
    box-shadow: 0 0 10px #edbe38;
  }

  .badge-text {
    font-family: "Outfit", sans-serif;
    font-size: 0.75rem;
    font-weight: 800;
    color: #ffffff;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
}

.animate-pulse-gold {
  animation: pulseGold 2.2s infinite ease-in-out;
}

@keyframes pulseGold {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
    box-shadow: 0 0 10px #edbe38;
  }
  50% {
    opacity: 0.5;
    transform: scale(1.25);
    box-shadow: 0 0 16px #edbe38;
  }
}

.page-title-group {
  .page-title {
    font-family: "Outfit", sans-serif;
    font-size: 1.35rem;
    font-weight: 800;
    color: #ffffff;
    letter-spacing: -0.01em;
    line-height: 1.2;
    text-transform: capitalize;
    text-shadow: 0 2px 10px rgba(10, 15, 41, 0.4);
  }

  .page-subtitle {
    font-size: 0.72rem;
    color: rgba(255, 255, 255, 0.65);
    font-weight: 500;
    margin-top: 2px;
    letter-spacing: 0.04em;
  }
}

/* Right Control Cluster */
.header-right-controls {
  animation: headerRightFade 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

@keyframes headerRightFade {
  0% {
    opacity: 0;
    transform: translateX(15px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
}

.system-status-chip {
  background: rgba(86, 189, 163, 0.12);
  border: 1px solid rgba(86, 189, 163, 0.35);
  padding: 0.4rem 0.95rem;
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 700;
  color: #ffffff;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 12px rgba(10, 15, 41, 0.1);

  .status-seafoam-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background-color: #56bda3;
    display: inline-block;
    box-shadow: 0 0 8px #56bda3;
    animation: statusPulse 2s infinite ease-in-out;
  }

  .text-seafoam {
    color: #56bda3 !important;
  }
}

@keyframes statusPulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.2);
  }
}

.quick-icon-btn {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #ffffff;
  cursor: pointer;
  position: relative;
  backdrop-filter: blur(10px);
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover {
    background: rgba(255, 255, 255, 0.22);
    color: #0097d7;
    transform: translateY(-2px);
    border-color: rgba(0, 151, 215, 0.5);
    box-shadow: 0 6px 16px rgba(0, 151, 215, 0.25);
  }

  .icon-indicator {
    position: absolute;
    top: 9px;
    right: 9px;
    width: 8px;
    height: 8px;
    background-color: #edbe38;
    border-radius: 50%;
    border: 2px solid #1b49a3;
    box-shadow: 0 0 6px #edbe38;
  }
}

.header-divider {
  width: 1px;
  height: 28px;
  background: rgba(255, 255, 255, 0.18);
  margin: 0 0.85rem;
}

/* User Profile Glass Card & Dropdown */
.user-profile-card {
  display: flex;
  align-items: center;
  padding: 0.35rem 0.95rem !important;
  border-radius: 24px !important;
  background: rgba(255, 255, 255, 0.12) !important;
  border: 1px solid rgba(255, 255, 255, 0.22) !important;
  text-decoration: none !important;
  backdrop-filter: blur(10px);
  box-shadow: 0 6px 18px rgba(10, 15, 41, 0.12) !important;
  transition: all 0.28s cubic-bezier(0.16, 1, 0.3, 1);

  &::after {
    display: none !important;
  }

  &:hover,
  &.show {
    background: rgba(255, 255, 255, 0.22) !important;
    border-color: rgba(0, 151, 215, 0.6) !important;
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 151, 215, 0.25) !important;

    .dropdown-arrow {
      color: #0097d7 !important;
      transform: rotate(180deg);
    }
  }

  .dropdown-arrow {
    transition: transform 0.25s ease, color 0.25s ease;
  }
}

.user-avatar-wrapper {
  position: relative;

  .user-avatar {
    width: 36px;
    height: 36px;
    border: 2px solid rgba(255, 255, 255, 0.8) !important;
    background-color: #ffffff;
    object-fit: cover;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }

  .avatar-status-online {
    position: absolute;
    bottom: 0;
    right: 0;
    width: 9.5px;
    height: 9.5px;
    background-color: #56bda3 !important;
    border: 2px solid #1b49a3;
    border-radius: 50%;
    box-shadow: 0 0 6px #56bda3;
  }
}

.user-info-text {
  display: flex !important;
  flex-direction: column !important;
  justify-content: center !important;
  text-align: left;
  line-height: 1.2;
  margin-right: 4px;

  .user-name {
    display: block !important;
    font-size: 0.85rem !important;
    font-weight: 800 !important;
    color: #ffffff !important;
    white-space: nowrap !important;
    letter-spacing: 0.01em;
  }

  .user-role {
    display: block !important;
    font-size: 0.68rem !important;
    color: rgba(255, 255, 255, 0.7) !important;
    font-weight: 600 !important;
    white-space: nowrap !important;
  }
}

/* Custom Profile Dropdown Menu */
.custom-profile-dropdown {
  display: none;
  position: absolute !important;
  top: 100% !important;
  right: 0 !important;
  left: auto !important;
  z-index: 2000 !important;
  border: 1px solid rgba(10, 15, 41, 0.12) !important;
  border-radius: 18px !important;
  padding: 0.5rem 0 !important;
  min-width: 235px !important;
  margin-top: 0.6rem !important;
  background: #ffffff !important;
  box-shadow: 0 15px 40px rgba(10, 15, 41, 0.2) !important;
}

.dropdown.show .custom-profile-dropdown,
.custom-profile-dropdown.show {
  display: block !important;
}

.dropdown-user-header {
  .text-navy {
    color: #0a0f29;
    font-family: "Outfit", sans-serif;
  }
}

.dropdown-item {
  font-size: 0.85rem;
  font-weight: 600;
  color: #414042;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(58, 103, 177, 0.06);
    color: #3a67b1;
    padding-left: 1.75rem;

    i {
      transform: scale(1.15);
    }
  }

  i {
    transition: transform 0.2s ease;
  }
}

.logout-item {
  color: #dc2626 !important;

  &:hover {
    background-color: #fef2f2 !important;
    color: #dc2626 !important;
  }
}

.animated--slide-in {
  animation: slideInDown 0.22s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slideInDown {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Reduced Motion Support */
@media (prefers-reduced-motion: reduce) {
  .header-tech-grid,
  .glow-aqua-left,
  .glow-gold-right,
  .hdr-curve,
  .header-shimmer-wave,
  .status-seafoam-dot,
  .badge-dot,
  .header-left-identity,
  .header-right-controls {
    animation: none !important;
    transform: none !important;
    opacity: 1 !important;
  }
}

/* Mobile Responsiveness for Header & User Profile Box */
@media (max-width: 768px) {
  .natcon-header-wrapper {
    padding: 0.5rem 0.5rem 0 !important;
  }

  .natcon-topbar {
    padding: 0 0.6rem !important;
    height: 4.25rem !important;
    border-radius: 16px !important;
  }

  .header-left-identity {
    min-width: 0 !important;
    flex: 1 1 auto !important;
    overflow: hidden !important;
  }

  .sidebar-toggle-btn {
    width: 38px !important;
    height: 38px !important;
    min-width: 38px !important;
    margin-right: 0.4rem !important;
  }

  .page-title-group {
    min-width: 0 !important;
    overflow: hidden !important;

    .page-title {
      font-size: 0.95rem !important;
      white-space: nowrap !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
      max-width: 120px !important;
    }
  }

  .header-right-controls {
    flex-shrink: 0 !important;
    margin-left: auto !important;
  }

  .user-profile-card {
    padding: 0.25rem 0.5rem !important;
    border-radius: 18px !important;

    .user-avatar-wrapper .user-avatar {
      width: 30px !important;
      height: 30px !important;
    }
  }

  .user-info-text {
    .user-name {
      max-width: 75px !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
      white-space: nowrap !important;
      font-size: 0.8rem !important;
    }

    .user-role {
      display: none !important;
    }
  }
}

@media (max-width: 480px) {
  .user-info-text {
    display: none !important;
  }

  .page-title-group .page-title {
    max-width: 90px !important;
  }
}
</style>